import { computed }                                                                                                   from '@vue/composition-api';
import { ElForm }                                                                                                     from 'element-ui/types/form';
import { useGlobals }                                                                                                 from '@/hooks/use-globals';
import { AddUserForm, ChangePasswordPayload, EditPasswordForm, EditUserForm, EditUserRequest, PostUserRequest, User } from '@/views/users/users.types';
import { messageBoxErrors }                                                                                           from '@/views/users/users.variables';
import { MessageBox }                                                                                                 from '@/components/message-box/message-box';
import { Computed }                                                                                                   from '@/types';
import { quietlyHandleError }                                                                                         from '@/helpers/helpers';
import { Notification }                                                                                               from '@/components/notification/notification';

interface UseUser {
  users: Computed<User[]>;
  getUsers: () => Promise<void>;
  addUser: (form: ElForm, model: AddUserForm) => Promise<void>;
  editUser: (form: ElForm, model: EditUserForm) => Promise<void>;
  editPassword: (form: ElForm, model: EditPasswordForm) => Promise<void>;
  removeUser: (user: User) => Promise<void>;
}

export const useUser = (): UseUser => {
  const { store, i18n } = useGlobals();
  const users: Computed<User[]> = computed((): User[] => store.state.views.vUsers.users);

  const getUsers = async () => {
    try {
      await store.dispatch('views/vUsers/getUsers');
    } catch (error) {
      throw new Error(error);
    } finally {
      store.commit('views/vUsers/setIsDataLoading', false);
    }
  };

  const addUser = async (form: ElForm, model: AddUserForm): Promise<void> => {
    store.commit('views/vUsers/setServerErrors', []);

    await form.validate(async (valid: boolean): Promise<void> => {
      if (valid) {
        const user: PostUserRequest = {
          firstName: model.firstName,
          lastName: model.lastName,
          login: model.login,
          position: model.position,
          email: model.email,
          status: model.status.toUpperCase(),
          password: model.password,
          activeApplications: [ 'PRORMS' ],
        };
        await store.dispatch('views/vUsers/addUser', user);
        if (store.state.views.vUsers.serverErrors.length) {
          await form.validate();
        }
      }
    });
  };

  const editUser = async (form: ElForm, model: EditUserForm): Promise<void> => {
    store.commit('views/vUsers/setServerErrors', []);

    const selectedUserUuid: string = store.state.views.vUsers.modal.form.selectedUuid;
    const selectedUserApplications: string[] = store.state.views.vUsers.users.filter((user: EditUserRequest) => selectedUserUuid === user.uuid)[ 0 ].activeApplications;

    await form.validate(async (valid: boolean): Promise<void> => {
      if (valid) {
        const user: EditUserRequest = {
          firstName: model.firstName,
          lastName: model.lastName,
          position: model.position,
          email: model.email,
          status: model.status.toUpperCase(),
          uuid: selectedUserUuid,
          activeApplications: [ ...selectedUserApplications ],
        };
        await store.dispatch('views/vUsers/editUser', user);
        if (store.state.views.vUsers.serverErrors.length) {
          await form.validate();
        }
      }
    });
  };

  const editPassword = async (form: ElForm, model: EditPasswordForm): Promise<void> => {
    store.commit('views/vUsers/setServerErrors', []);

    await form.validate(async (valid: boolean): Promise<void> => {
      if (valid) {
        const { password } = model;
        const payload: ChangePasswordPayload = {
          uuid: store.state.views.vUsers.modal.form.selectedUuid,
          password,
        };
        await store.dispatch('views/vUsers/changePassword', payload);
        if (store.state.views.vUsers.serverErrors.length) {
          await form.validate();
        }
      }
    });
  };

  const removeUser = async (user: User): Promise<void> => {
    try {
      await MessageBox.confirm(
        i18n.t('vUsers.messageBox.delete.message') as string,
        i18n.t('vUsers.messageBox.delete.title', [ user.firstName, user.lastName ]) as string,
        {},
      );
      await store.dispatch('views/vUsers/removeUser', user);
    } catch (error) {
      if (messageBoxErrors.includes(error)) {
        quietlyHandleError(error, 'Cancel modal');
      } else {
        Notification.error('vUsers.notification.error.title', i18n.t('vUsers.notification.error.removeUser') as string);
        throw new Error(error);
      }
    }
  };

  return {
    users,
    getUsers,
    addUser,
    editUser,
    removeUser,
    editPassword,
  };
};
